// Types for Investment Management Operations
// Matches backend DTOs from AdminICTDto.ts (camelCase convention)

import type { RetirementContributionType } from '../../entities/membership/hooks/investments/useTestICTDeposit';
import type {
    RetirementDistributionCreateType,
    TaxWithholding,
} from '../../entities/membership/hooks/investments/useTestICTWithdrawal';

export type IctStatus =
    | 'INITIATED'
    | 'WAITING_COMPLETED'
    | 'COMPLETED'
    | 'FAILED'
    | 'FAILED_INCONSISTENT'
    | 'CANCELLED';

export const ICT_PENDING_STATUSES: IctStatus[] = [
    'INITIATED',
    'WAITING_COMPLETED',
];
export const ICT_ERROR_STATUSES: IctStatus[] = [
    'FAILED',
    'FAILED_INCONSISTENT',
];
export const ICT_TERMINAL_STATUSES: IctStatus[] = [
    'COMPLETED',
    'FAILED',
    'FAILED_INCONSISTENT',
    'CANCELLED',
];

/**
 * State log entry for tracking transfer state changes
 */
export interface StateLogEntry {
    time: string; // ISO timestamp
    source: 'ORCHESTRATION' | 'APEX' | 'SYNCTERA_PAY';
    state: string;
    actor?: string;
    message?: string;
    metadata?: Record<string, unknown>;
}

/**
 * Retirement details persisted on an ICT transfer: retirement_distribution on
 * withdrawals, retirement_contribution on deposits. Both shapes share one field, so
 * everything is optional and each field notes which transfer type populates it.
 */
export interface AdminICTRetirementDetailsDto {
    /** Distribution type on withdrawals, contribution type on deposits */
    type?: RetirementDistributionCreateType | RetirementContributionType;
    /** Deposits only: tax year the contribution is applied to */
    taxYear?: number;
    /** Withdrawals only: requested federal withholding, as an amount or a percentage */
    federalTaxWithholding?: TaxWithholding;
    /** Withdrawals only: requested state withholding, as an amount or a percentage */
    stateTaxWithholding?: TaxWithholding;
    /** Withdrawals only: member waived state withholding */
    stateWithholdingWaiver?: boolean;
}

/**
 * Merged ICT transfer detail (combines deposits and withdrawals)
 *
 * settlementDate = COALESCE(reconSettlementDate, expectedSettlementDate)
 * - For reconciled transactions: uses authoritative reconSettlementDate
 * - For pending transactions: uses estimated expectedSettlementDate
 */
export interface AdminICTTransferDetailDto {
    // Transfer identification
    idempotencyKey: string;
    workflowId: string;
    settlementDate: string;
    transferType: 'DEPOSIT' | 'WITHDRAWAL';

    // User/Account information
    userId: string;
    membershipId: string;

    // Account details
    bankAccountId: string;
    apexAccountId: string;

    // Amount information
    // Deposits: amountCents; Withdrawals: COALESCE(settlement, actual, requested)
    amountCents: number | null;
    requestedAmountCents?: number | null; // Withdrawals only: member-entered gross
    actualAmountCents?: number | null; // Withdrawals only: Apex-confirmed gross
    settlementAmountCents?: number | null; // Withdrawals only: net cash from batch recon, credited via SyncteraPay
    taxAmountWithheldCents?: number | null; // Withdrawals only: gross minus settlement
    fullDisbursement?: boolean | null; // Withdrawals only

    // Transfer state
    status: IctStatus;

    // External IDs for tracking
    apexIctId: string | null;
    syncteraPayId: string | null;
    syncteraTransactionUuid: string | null;

    // State details
    apexCurrentState: string | null;
    syncteraPayCurrentState: string | null;

    // Overall completion
    completedAt: Date | null;

    // Expected settlement (calculated at creation based on calendar)
    expectedSettlementDate: string | null; // YYYY-MM-DD

    // Reconciliation (authoritative settlement date when reconciled)
    reconSettlementDate: string | null; // YYYY-MM-DD
    reconciledAt: Date | null;

    // Apex tracking
    apexPostedAt: Date | null;
    apexCompletedAt: Date | null;

    // Synctera tracking
    syncteraPayPendingCreatedAt: Date | null;
    syncteraPayPostedAt: Date | null;
    syncteraPostedDate: string | null; // YYYY-MM-DD
    syncteraPayEffectiveDate: string | null; // YYYY-MM-DD

    // Timestamps
    createdAt: Date;
    updatedAt: Date;

    // Error information
    workflowError: Record<string, unknown> | null;

    // Audit/State tracking
    stateLog: Array<Record<string, unknown>>;

    // Additional context
    memo: string | null;
    retirementDetails: AdminICTRetirementDetailsDto | null;
}

/**
 * Request DTO for cancelling an ICT transfer
 */
export interface CancelICTTransferRequestDto {
    idempotencyKey: string;
    transferType: 'DEPOSIT' | 'WITHDRAWAL';
    cancellationReason: string;
    adminUserEmail: string;
}

/**
 * Response from cancel transfer endpoint
 */
export interface CancelICTTransferResponseDto {
    success: boolean;
    message: string;
    transfer: {
        idempotencyKey: string;
        transferType: 'DEPOSIT' | 'WITHDRAWAL';
        status: string;
        wasCancellable: boolean;
        wasCancelled: boolean;
    };
}

/**
 * Request DTO for manually triggering ICT batch reconciliation
 */
export interface TriggerBatchReconRequestDto {
    settlementDate: string; // YYYY-MM-DD format
    messageId?: string;
    dryRun?: boolean;
}

/**
 * Metadata for a reconciliation report file
 */
export interface ReconFileDto {
    fileName: string;
    sizeBytes: number;
}

/**
 * Response DTO for manual batch reconciliation trigger
 */
export interface TriggerBatchReconResponseDto {
    success: boolean;
    message: string;
    webhook?: {
        messageId?: string;
        batchId: string | null;
        settlementDate: string;
        status: string;
    };
    result?: {
        success: boolean;
        batchId: string | null;
        apexReconFile?: ReconFileDto;
        syncteraReconFile?: ReconFileDto;
        breakCount?: number;
        settlement?: {
            totalDeposits: number;
            totalWithdrawals: number;
            netAmount: number;
            direction: string;
        };
    };
    error?: string;
    logs: string[];
}

/**
 * Wire direction based on net amount of completed transfers
 * From database enum
 */
export type NetWireDirection =
    | 'WIRE_EB_TO_APEX'
    | 'WIRE_APEX_TO_EB'
    | 'NO_WIRE';

/**
 * High-level summary for transfers endpoint
 * All metrics are for COMPLETED transfers only
 */
export interface TransfersSummary {
    totalCompleteCount: number;
    depositCompleteCount: number;
    withdrawalCompleteCount: number;
    totalDepositCompleteAmountCents: number;
    totalWithdrawalCompleteAmountCents: number;
    netCompleteAmountCents: number;
    wireDirection: NetWireDirection;
    isPastEod: boolean;
}

/**
 * Response containing merged ICT transfers for a settlement date
 */
export interface AdminICTTransfersDto {
    settlementDate: string;
    transfers: AdminICTTransferDetailDto[];
    summary: TransfersSummary;
}

/**
 * Flat daily summary row (one row per settlement date)
 * Wide format with all deposit/withdrawal metrics in a single row
 *
 * settlementDate = COALESCE(reconSettlementDate, expectedSettlementDate)
 * - For reconciled transactions: uses authoritative reconSettlementDate
 * - For pending transactions: uses estimated expectedSettlementDate
 */
export interface AdminICTDailySummaryRowDto {
    // Date information
    settlementDate: string;
    dayOfWeek: string;
    isTradingDay: boolean;
    isBankingDay: boolean;
    holidayName: string | null;

    // Deposit metrics by status
    depositPendingCount: number;
    depositPendingAmountCents: number;
    depositCompletedCount: number;
    depositCompletedAmountCents: number;
    depositErrorCount: number;
    depositErrorAmountCents: number;
    depositCancelledCount: number;
    depositCancelledAmountCents: number;

    // Withdrawal metrics by status
    withdrawalPendingCount: number;
    withdrawalPendingAmountCents: number;
    withdrawalCompletedCount: number;
    withdrawalCompletedAmountCents: number;
    withdrawalErrorCount: number;
    withdrawalErrorAmountCents: number;
    withdrawalCancelledCount: number;
    withdrawalCancelledAmountCents: number;

    // Net calculations (completed transactions only)
    netCompleteAmountCents: number;
    netCompleteCount: number;
}

// --- Reconciliation Status Types ---

export type IctReconStatus =
    | 'IN_PROGRESS'
    | 'PENDING_REVIEW'
    | 'COMPLETED'
    | 'COMPLETED_MANUAL'
    | 'COMPLETED_NO_ACTIVITY'
    | 'COMPLETED_WEEKEND_OR_HOLIDAY'
    | 'FAILED';

export interface AdminICTReconNotFoundDto {
    status: null;
    settlementDate: string;
    message: string;
}

export type ReconWireDirection =
    | 'OUTGOING_WIRE_EB_TO_APEX'
    | 'INCOMING_WIRE_APEX_TO_EB'
    | 'NO_WIRE';

export interface AdminICTReconStatusDto {
    status: IctReconStatus;
    settlementDate: string;
    createdBy: string;
    apexBatchId: string | null;
    workflowId: string;
    currentStep: string | null;

    breakCount: number;
    breaks: ReconBreakDto[] | null;
    netAmountCents: number | null;
    wireDirection: ReconWireDirection | null;

    apexReportS3Uri: string | null;
    apexReportUploadedToS3At: string | null;
    syncteraReportS3Uri: string | null;
    syncteraReportUploadedToS3At: string | null;

    apexSftpUploadStatus: string | null;
    apexSftpUploadedAt: string | null;
    apexSftpPath: string | null;
    syncteraSftpUploadStatus: string | null;
    syncteraSftpUploadedAt: string | null;
    syncteraSftpPath: string | null;

    emailSentAt: string | null;
    emailSubject: string | null;
    emailBody: string | null;
    emailSentTo: string[] | null;
    emailSentCc: string[] | null;
    /** Default To addresses for Gmail compose. */
    emailDefaultRecipients?: string[];
    /** Default CC addresses for Gmail compose (UI removes logged-in sender). */
    emailDefaultCc?: string[];

    reviewedBy: string | null;
    reviewedAt: string | null;
    reviewNotes: string | null;

    stateLog: ReconStateLogEntry[];
    error: unknown;

    createdAt: string;
    updatedAt: string;
}

export interface ReconStateLogEntry {
    timestamp: string;
    step: string;
    status: string;
    message?: string;
    actor?: string;
    metadata?: Record<string, unknown>;
}

export interface ReconBreakDto {
    externalId: string;
    source: 'Apex' | 'Contra' | 'Client';
    breakReason: string;
    amount: number;
    transferDirection: string;
}

/**
 * Request/response types for ICT recon operations.
 * See ict_recon_ui_spec.md for full API reference.
 */

export interface SendWireEmailRequestDto {
    settlementDate: string;
    subject?: string;
    body?: string;
    recipients?: string[];
    cc?: string[];
    reviewedBy?: string;
    reviewNotes?: string;
}

export interface UploadToSftpRequestDto {
    settlementDate: string;
}

export interface ManualResolutionRequestDto {
    settlementDate: string;
    adminUserEmail: string;
    reviewNotes?: string;
}

export interface ReconMutationResponseDto {
    success: boolean;
    message: string;
}

export interface UploadReconFileRequestDto {
    settlementDate: string;
    fileType: 'apex' | 'synctera';
    fileContentBase64: string;
}

// --- Snapshot Comparison Types ---

/**
 * Single row in the snapshot comparison result.
 * Compares apex_snapshot, synctera_snapshot, and FF deposit+withdrawal snapshots.
 */
export interface SnapshotComparisonRow {
    externalId: string;
    apexAmount: number | null;
    syncteraAmount: number | null;
    ffAmount: number | null;
    apexDirection: string | null;
    syncteraDirection: string | null;
    ffDirection: string | null;
    apexStatus: 'OK' | 'MISSING';
    syncteraStatus: 'OK' | 'MISSING';
    ffStatus: 'OK' | 'MISSING';
    apexInvestmentAccount: string | null;
    syncteraAccountId: string | null;
    breakType:
        | 'OK'
        | 'AMOUNT_MISMATCH'
        | 'MISSING_RECORD'
        | 'IGNORED_EXTERNAL_ACCOUNT';
}

/**
 * Response for the snapshot comparison endpoint
 */
export interface AdminICTSnapshotComparisonDto {
    settlementDate: string;
    comparisons: SnapshotComparisonRow[];
    summary: {
        totalRecords: number;
        recordsWithBreaks: number;
        apexCount: number;
        syncteraCount: number;
        ffCount: number;
        apexTotalAmount: number;
        syncteraTotalAmount: number;
        ffTotalAmount: number;
    };
}

// ---------------------------------------------------------------------------
// Cash Journal Types
// ---------------------------------------------------------------------------

export type CashJournalStatus =
    | 'INITIATED'
    | 'PROCESSING'
    | 'COMPLETED'
    | 'CANCELED'
    | 'FAILED'
    | 'SKIPPED';

export const CASH_JOURNAL_TERMINAL_STATUSES: CashJournalStatus[] = [
    'COMPLETED',
    'CANCELED',
    'FAILED',
    'SKIPPED',
];

export const CASH_JOURNAL_NON_TERMINAL_STATUSES: CashJournalStatus[] = [
    'INITIATED',
    'PROCESSING',
];

/**
 * Admin view of a single cash journal transfer.
 * Mirrors AdminCashJournalTransferDto from the backend.
 */
export interface AdminCashJournalTransferDto {
    clientTransferId: string;
    workflowId: string | null;
    purpose: string;
    initiationSource: string;

    status: CashJournalStatus;
    statusUpdatedAt: string;
    completedAt: string | null;
    workflowError: Record<string, unknown> | null;

    apexCashJournalId: string | null;
    apexCurrentState: string | null;
    apexStateUpdatedAt: string | null;
    apexPostedAt: string | null;
    apexCompletedAt: string | null;

    fromApexAccountId: string;
    fromApexAccountNumber: string;
    toApexAccountId: string;
    toApexAccountNumber: string;

    amountCents: number | null;
    fullDisbursement: boolean | null;

    membershipId: string;
    userId: string | null;

    triggeringIctDepositIdempotencyKey: string | null;

    stateLog: Array<Record<string, unknown>>;

    createdAt: string;
    updatedAt: string;
}

export interface CancelCashJournalRequestDto {
    clientTransferId: string;
    cancellationReason: string;
    adminUserEmail: string;
}

export interface ForceApproveCashJournalRequestDto {
    clientTransferId: string;
}

export interface ForceRejectCashJournalRequestDto {
    clientTransferId: string;
    reason?: string;
}

export interface CashJournalActionResponseDto {
    success: boolean;
    message: string;
    clientTransferId: string;
    apexCashJournalId: string | null;
    apexResponse?: unknown;
}

export type ICTWithdrawalRequestStatus =
    | 'REQUESTED'
    | 'SOLD'
    | 'SETTLED'
    | 'TRANSFER_SUBMITTED'
    | 'TRANSFER_APPROVED'
    | 'DONE'
    | 'ERROR';

export type AdminICTWithdrawalRequestMember = {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    legalFirstName: string | null;
    legalLastName: string | null;
} & Record<string, unknown>;

export interface AdminICTWithdrawalRequestApexSnippet {
    nickname: string | null;
    accountNumber: string;
    registrationType: string;
}

export interface AdminICTWithdrawalRequestBankSnippet {
    accountType: string;
    syncteraAccountId: string;
}

export interface AdminICTWithdrawalRequestRow {
    id: string;
    userId: string;
    membershipId: string;
    fromApexAccountId: string;
    toBankAccountId: string;
    /** Null when amount is unknown until liquidation (full disbursement). */
    amountCents: number | null;
    isLiquidated: boolean;
    status: ICTWithdrawalRequestStatus;
    completedBy: string | null;
    approvedBy: string | null;
    distributionReason: string | null;
    taxWithholding: number | null;
    createdAt: string;
    updatedAt: string;
    user?: AdminICTWithdrawalRequestMember | null;
    fromApexAccount: AdminICTWithdrawalRequestApexSnippet | null;
    toBankAccount: AdminICTWithdrawalRequestBankSnippet | null;
}

export interface PatchICTWithdrawalRequestBody {
    status?: ICTWithdrawalRequestStatus;
    isLiquidated?: boolean;
    completedBy?: string;
    approvedBy?: string;
    distributionReason?: string;
    taxWithholding?: number | null;
}
